// src/components/StreamScheduleEditor.js
// ============================================================================
// WEEKLY STREAM SCHEDULE EDITOR (evergreen Step 1.2, 2026-09-22)
//
// A creator publishes the days and times they go live. Saved through the
// setMyStreamSchedule callable; the server validates and computes the next
// slot in the creator's timezone, so this component never does timezone
// math. Mounted in the Streaming Hub "Your Stream" section and the Streams
// page "Your channel" modal. The channel page, the public profile and Home
// "starting soon" all read the same stored schedule.
// ============================================================================

import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, TextInput, ActivityIndicator, StyleSheet } from 'react-native';
import { getFunctions, httpsCallable } from 'firebase/functions';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DURATIONS = [60, 120, 180, 240];
const TIMEZONES = [
  ['America/Chicago', 'Central'],
  ['America/New_York', 'Eastern'],
  ['America/Denver', 'Mountain'],
  ['America/Los_Angeles', 'Pacific'],
  ['Europe/London', 'UK'],
  ['Europe/Berlin', 'Central EU'],
];
const HHMM_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

const fmtNext = (ms) => {
  if (!ms) return null;
  try {
    return new Date(ms).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch (_) { return null; }
};

/** Local shape: { [dow]: { hhmm, durationMin } } */
const fromSchedule = (schedule) => {
  const byDay = {};
  for (const s of (schedule?.slots || [])) {
    if (byDay[s.dow]) continue; // one slot per day in this editor
    byDay[s.dow] = { hhmm: s.hhmm, durationMin: s.durationMin || 120 };
  }
  return byDay;
};

export default function StreamScheduleEditor({ schedule, nextStreamAtMs, onSaved, showError, compact = false }) {
  const [tz, setTz] = useState(schedule?.tz || 'America/Chicago');
  const [byDay, setByDay] = useState(() => fromSchedule(schedule));
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [nextMs, setNextMs] = useState(nextStreamAtMs || null);

  // Re-seed when the parent finishes loading the stored schedule.
  useEffect(() => {
    setTz(schedule?.tz || 'America/Chicago');
    setByDay(fromSchedule(schedule));
    setNextMs(nextStreamAtMs || null);
    setDirty(false);
  }, [schedule, nextStreamAtMs]);

  const enabledDays = useMemo(() => Object.keys(byDay).map(Number).sort((a, b) => a - b), [byDay]);
  const invalid = enabledDays.some((d) => !HHMM_RE.test(byDay[d]?.hhmm || ''));

  const toggleDay = (d) => {
    setByDay((prev) => {
      const next = { ...prev };
      if (next[d]) delete next[d];
      else next[d] = { hhmm: enabledDays.length ? (prev[enabledDays[0]]?.hhmm || '19:00') : '19:00', durationMin: 120 };
      return next;
    });
    setDirty(true);
  };
  const setTime = (d, hhmm) => { setByDay((p) => ({ ...p, [d]: { ...p[d], hhmm } })); setDirty(true); };
  const setDuration = (d, durationMin) => { setByDay((p) => ({ ...p, [d]: { ...p[d], durationMin } })); setDirty(true); };

  const save = async () => {
    if (invalid) { showError?.('Check your times', 'Use 24-hour HH:MM, for example 19:30.'); return; }
    try {
      setSaving(true);
      const slots = enabledDays.map((d) => ({ dow: d, hhmm: byDay[d].hhmm, durationMin: byDay[d].durationMin }));
      const fn = httpsCallable(getFunctions(), 'setMyStreamSchedule');
      const { data } = await fn({ schedule: slots.length ? { tz, slots } : null });
      setNextMs(data?.nextStreamAtMs || null);
      setDirty(false);
      onSaved?.(data);
    } catch (err) {
      showError?.('Could not save schedule', err?.message || 'Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <View style={[styles.wrap, compact && styles.wrapCompact]}>
      <View style={styles.headRow}>
        <Text style={styles.title}>Weekly schedule</Text>
        {nextMs ? <Text style={styles.next}>Next: {fmtNext(nextMs)}</Text> : <Text style={styles.nextMuted}>No schedule published</Text>}
      </View>
      <Text style={styles.hint}>Pick the days you go live. Followers see your next stream on your channel and on Home.</Text>

      <View style={styles.dayRow}>
        {DAYS.map((label, d) => {
          const on = !!byDay[d];
          return (
            <TouchableOpacity key={label} style={[styles.dayChip, on && styles.dayChipOn]} onPress={() => toggleDay(d)}>
              <Text style={[styles.dayChipTxt, on && styles.dayChipTxtOn]}>{label}</Text>
            </TouchableOpacity>
          );
        })}
      </View>

      {enabledDays.map((d) => (
        <View key={d} style={styles.slotRow}>
          <Text style={styles.slotDay}>{DAYS[d]}</Text>
          <TextInput
            style={[styles.timeInput, !HHMM_RE.test(byDay[d].hhmm || '') && styles.timeInputBad]}
            value={byDay[d].hhmm}
            onChangeText={(t) => setTime(d, t.replace(/[^0-9:]/g, '').slice(0, 5))}
            placeholder="19:30"
            placeholderTextColor="#555"
            maxLength={5}
          />
          <View style={styles.durRow}>
            {DURATIONS.map((m) => (
              <TouchableOpacity key={m} style={[styles.durChip, byDay[d].durationMin === m && styles.durChipOn]} onPress={() => setDuration(d, m)}>
                <Text style={[styles.durChipTxt, byDay[d].durationMin === m && styles.durChipTxtOn]}>{m / 60}h</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      ))}

      <View style={styles.tzRow}>
        <Text style={styles.tzLabel}>Time zone</Text>
        {TIMEZONES.map(([id, label]) => (
          <TouchableOpacity key={id} style={[styles.tzChip, tz === id && styles.tzChipOn]} onPress={() => { setTz(id); setDirty(true); }}>
            <Text style={[styles.tzChipTxt, tz === id && styles.tzChipTxtOn]}>{label}</Text>
          </TouchableOpacity>
        ))}
      </View>

      <View style={styles.actionRow}>
        <TouchableOpacity style={[styles.saveBtn, (!dirty || saving || invalid) && { opacity: 0.5 }]} onPress={save} disabled={!dirty || saving || invalid}>
          {saving ? <ActivityIndicator size="small" color="#000" /> : <Text style={styles.saveBtnTxt}>{enabledDays.length ? 'Save schedule' : 'Clear schedule'}</Text>}
        </TouchableOpacity>
        <Text style={styles.actionHint}>Times are 24-hour in your time zone.</Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: { marginTop: 14, padding: 12, borderRadius: 10, backgroundColor: '#0f0f0f', borderWidth: 1, borderColor: '#222' },
  wrapCompact: { padding: 10 },
  headRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6 },
  title: { color: '#EDEDED', fontSize: 14, fontWeight: '800' },
  next: { color: '#FCD000', fontSize: 12, fontWeight: '700' },
  nextMuted: { color: '#666', fontSize: 12 },
  hint: { color: '#888', fontSize: 12, marginTop: 4 },
  dayRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 10 },
  dayChip: { paddingVertical: 6, paddingHorizontal: 10, borderRadius: 14, backgroundColor: '#1a1a1a', borderWidth: 1, borderColor: '#2a2a2a' },
  dayChipOn: { backgroundColor: '#FCD000', borderColor: '#FCD000' },
  dayChipTxt: { color: '#aaa', fontSize: 12, fontWeight: '700' },
  dayChipTxtOn: { color: '#000' },
  slotRow: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginTop: 8 },
  slotDay: { color: '#EDEDED', fontSize: 13, fontWeight: '700', width: 36 },
  timeInput: { width: 72, backgroundColor: '#161616', borderWidth: 1, borderColor: '#2a2a2a', borderRadius: 8, color: '#EDEDED', paddingVertical: 6, paddingHorizontal: 8, fontSize: 13, textAlign: 'center' },
  timeInputBad: { borderColor: '#b33' },
  durRow: { flexDirection: 'row', gap: 4 },
  durChip: { paddingVertical: 5, paddingHorizontal: 8, borderRadius: 10, backgroundColor: '#161616', borderWidth: 1, borderColor: '#2a2a2a' },
  durChipOn: { borderColor: '#FCD000' },
  durChipTxt: { color: '#888', fontSize: 11, fontWeight: '700' },
  durChipTxtOn: { color: '#FCD000' },
  tzRow: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginTop: 12 },
  tzLabel: { color: '#888', fontSize: 12, marginRight: 4 },
  tzChip: { paddingVertical: 4, paddingHorizontal: 8, borderRadius: 10, backgroundColor: '#161616', borderWidth: 1, borderColor: '#2a2a2a' },
  tzChipOn: { borderColor: '#00e5ff' },
  tzChipTxt: { color: '#888', fontSize: 11, fontWeight: '700' },
  tzChipTxtOn: { color: '#00e5ff' },
  actionRow: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginTop: 12 },
  saveBtn: { backgroundColor: '#FCD000', borderRadius: 8, paddingVertical: 8, paddingHorizontal: 14 },
  saveBtnTxt: { color: '#000', fontSize: 13, fontWeight: '800' },
  actionHint: { color: '#666', fontSize: 11 },
});
